import { env } from "@/lib/env";
// import { getBoundingBoxFromCenter } from "@/lib/getBoundingBoxFromCenter/get_bounding_box";
// import { getRadiusByType } from "@/lib/getRadiusAndZoomByType/getRadiusByType";
// import { getZoomByType } from "@/lib/getRadiusAndZoomByType/getZoomByType";
// import { getLastSegment } from "@/lib/getSuggestionLastSegment/get_suggestion_last_segment";
import {
  ApiError,
  AutocompleteSuggestion,
  DEFAULT_SEARCH,
  PurposeEnum,
  Search,
  SortingEnum,
  TypeEnum,
} from "@/types/types";
import { ChangeEvent } from "react";
import { toast } from "react-toastify";
import { create } from "zustand";

const SEARCHENDPOINTURL = env.NEXT_SEARCH_API_URL as string;

interface SearchbarStore {
  search: Search;
  suggestions: AutocompleteSuggestion[];
  isSuggestionsOpen: boolean;
  setIsSuggestionsOpen: (value: boolean) => void;
  currentSuggestion: AutocompleteSuggestion | null;
  text: string;
  onTextChange: (
    e: ChangeEvent<HTMLInputElement>,
    locale: string,
    lat: string | null,
    long: string | null
  ) => void;
  getSuggestion: (
    text: string,
    locale: string,
    lat: string | null,
    long: string | null
  ) => Promise<void>;
  setCurrentSuggestion: (
    suggestion: AutocompleteSuggestion,
    locale: string
  ) => void;
  setType: (type: TypeEnum) => void;
  setPurpose: (purpose: PurposeEnum) => void;
  validateFields: () => void;
  validateWhereField: () => void;
  validateTypeField: () => void;
  validatePurposeField: () => void;
  fieldsErrors: {
    where: boolean;
    type: boolean;
    purpose: boolean;
  };
}

let debounceTimeout: ReturnType<typeof setTimeout>;

export const useSearchbarStore = create<SearchbarStore>((set, get) => ({
  search: DEFAULT_SEARCH,
  suggestions: [],
  currentSuggestion: null,
  isSuggestionsOpen: false,
  setIsSuggestionsOpen: (value) => {
    set({ isSuggestionsOpen: value });
  },
  text: "",
  onTextChange: (e, locale, lat, long) => {
    e.preventDefault();
    const text = e.target.value;
    set({ text: text });
    get().getSuggestion(text, locale, lat, long);
  },
  getSuggestion: async (text, locale, lat, long) => {
    if (debounceTimeout) clearTimeout(debounceTimeout);

    if (text.length < 1) {
      set({ suggestions: [], isSuggestionsOpen: false });
      return;
    }

    debounceTimeout = setTimeout(async () => {
      try {
        const resp = await fetch(
          `${SEARCHENDPOINTURL}/getAutocomplete?text=${encodeURIComponent(
            text
          )}&locale=${locale}`,
          {
            headers: {
              "Content-Type": "application/json",
              "x-viewer-lat": lat || "",
              "x-viewer-long": long || "",
            },
          }
        );
        if (resp.ok) {
          const data = (await resp.json()) as {
            locations_array: AutocompleteSuggestion[];
          };
          set({ suggestions: data.locations_array, isSuggestionsOpen: true });
        } else {
          const apiErr = (await resp.json()) as ApiError;
          throw new Error(apiErr.message);
        }
      } catch (err) {
        set({ suggestions: [], isSuggestionsOpen: false });
        const errorMessage =
          err instanceof Error ? err.message : "An unexpected error occurred.";
        toast(
          errorMessage === "Failed to fetch"
            ? "Unable to get location suggestions."
            : errorMessage,
          {
            type: "error",
          }
        );
        return;
      }
    }, 300);
  },
  setCurrentSuggestion: (suggestion, locale) => {
    set((state) => ({
      search: {
        ...state.search,
        location_url: suggestion.search_urls[locale],
        center: {
          lat: suggestion.geo_location.lat,
          lon: suggestion.geo_location.lon,
        },
        location_type: suggestion.type,
        bouding_box: {
          ...state.search.bouding_box,
          top_left_lat: suggestion.map_view.north,
          top_left_lon: suggestion.map_view.west,
          bottom_right_lat: suggestion.map_view.south,
          bottom_right_lon: suggestion.map_view.east,
        },
      },
      currentSuggestion: suggestion,
      text: suggestion.names[locale] ?? suggestion.names["en"],
      suggestions: [],
      isSuggestionsOpen: false,
    }));
  },
  setType: (type) => {
    set((state) => ({
      search: {
        ...state.search,
        type: type,
      },
    }));
  },
  setPurpose: (purpose) => {
    set((state) => ({
      search: {
        ...state.search,
        purpose: purpose,
      },
    }));
  },
  fieldsErrors: {
    where: false,
    type: false,
    purpose: false,
  },
  validateFields: () => {
    const currentSuggestion = get().currentSuggestion;
    // const location = get().search.url;
    const type = get().search.type;
    const purpose = get().search.purpose;
    if (currentSuggestion === null) {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          where: true,
        },
      }));
    } else {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          where: false,
        },
      }));
    }
    if (type === TypeEnum.DEFAULT) {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          type: true,
        },
      }));
    } else {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          type: false,
        },
      }));
    }
    if (purpose === PurposeEnum.DEFAULT) {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          purpose: true,
        },
      }));
    } else {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          purpose: false,
        },
      }));
    }
  },
  validateWhereField: () => {
    const currentSuggestion = get().currentSuggestion;
    // const location = get().search.url;
    if (currentSuggestion === null) {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          where: true,
        },
      }));
    } else {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          where: false,
        },
      }));
    }
  },
  validateTypeField: () => {
    const type = get().search.type;
    if (type === TypeEnum.DEFAULT) {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          type: true,
        },
      }));
    } else {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          type: false,
        },
      }));
    }
  },
  validatePurposeField: () => {
    const purpose = get().search.purpose;
    if (purpose === PurposeEnum.DEFAULT) {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          purpose: true,
        },
      }));
    } else {
      set((state) => ({
        ...state,
        fieldsErrors: {
          ...state.fieldsErrors,
          purpose: false,
        },
      }));
    }
  },
}));
